import { useCallback } from 'react'
import { useIntl } from 'react-intl'

import PriceChangeViewV2 from 'components/Common/PriceChangeView/PriceChangeViewV2'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'

interface Props {
  reason: 'property-fees' | 'upgrade-margin'
  oldDisplayPrice: number
  newDisplayPrice: number
  oldPropertyFees: number
  newPropertyFees: number
}

function PropertyLoyaltyUpgradeUnavailableModal({
  reason,
  oldDisplayPrice,
  newDisplayPrice,
  oldPropertyFees,
  newPropertyFees,
}: Props) {
  const intl = useIntl()
  const { resolve } = useModalElementContext<boolean | undefined>()
  const continueWithoutUpgrade = useCallback(() => resolve(true), [resolve])
  const message = reason === 'property-fees'
    ? intl.formatMessage({
        id: 'lpeugOSwa7k0XMlwzuNw',
        defaultMessage: 'The property fees changed before we could secure your stay, so your package upgrade can’t be applied. You can continue at the updated total or choose another package. Your upgrade hasn’t been used.',
        description: 'Modal message explaining that changed property fees invalidated a selected package upgrade before checkout',
      })
    : intl.formatMessage({
        id: 'IxP6o_cz3cmKEGNNFRlL',
        defaultMessage: 'Your package upgrade can’t be applied to this booking. You can continue with this package at its current price or choose another package. Your upgrade hasn’t been used.',
        description: 'Modal message explaining that a selected package upgrade was rejected before checkout while the target package remains available',
      })

  return <ModalBase size="S">
    <ModalHeader title={intl.formatMessage({
      id: 'MWNU1bhJDwHNC0k0obdN',
      defaultMessage: 'Your package upgrade is no longer available',
      description: 'Modal title shown on an offer page when a selected package upgrade becomes invalid before checkout',
    })}
    />
    <ModalBody>
      <ModalContent>
        <VerticalSpacer gap={24}>
          <BodyText as="p" variant="large">
            {message}
          </BodyText>
          <PriceChangeViewV2
            oldPrice={oldDisplayPrice}
            newPrice={newDisplayPrice}
            oldPropertyFees={oldPropertyFees}
            newPropertyFees={newPropertyFees}
          />
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
    <ModalFooter
      primaryActionProps={{
        children: intl.formatMessage({
          id: 'MLg7g98SmCIW0evmvjpT',
          defaultMessage: 'Continue without upgrade',
          description: 'Button label that continues an offer-page booking after its selected package upgrade became unavailable',
        }),
        onClick: continueWithoutUpgrade,
      }}
      secondaryActionProps={{
        children: intl.formatMessage({
          id: 'y1Ql2_MMZ062WQMQezuX',
          defaultMessage: 'Choose another package',
          description: 'Button label that closes the modal so the member can choose another package for their upgrade',
        }),
      }}
    />
  </ModalBase>
}

export default PropertyLoyaltyUpgradeUnavailableModal
